"use client"

/*
 * DEV HANDOFF - source-settings.tsx
 *
 * THE COMPLEX UI'S FEED SETTINGS - the same seven tabs the basic screen has,
 * reached through a route instead of directly, with three fields basic does not
 * get.
 *
 * WHY IT IS A SEPARATE PAGE AND NOT A PROP ON `feed-settings.tsx`: the two
 * differ in what they CONTAIN, not how they look, and they sit at different
 * places in their hierarchies. In basic the feed IS the automation, which is
 * why that screen is titled "Automation settings". Here the ROUTE is the
 * automation and this is one of its sources, so the same title would be wrong.
 * Sharing a title prop across that difference would hide it.
 *
 *   COMPLEX   route -> sources (many) -> destinations
 *             route-settings.tsx    THIS    destination-override.tsx
 *   BASIC     automation (= one source) -> destinations
 *             feed-settings.tsx             destination-override.tsx
 *
 * WHAT COMPLEX ADDS, all three from real gates in legacy rather than from the
 * screenshots alone:
 *
 * 1. **Update windows** (Updates) - `InputUpdatePanel.jsx:96`,
 *    `ROUTE_VIEW_ADVANCED` only. Day toggles plus a time range per window.
 * 2. **Next post** (General) - `InputDetailPanel.jsx:96`, complex AND a source
 *    that was inactive at load and is active now. A transient question about
 *    the backlog, not a standing setting, which is why `justActivated` exists.
 *    Its option verb tracks "When should we post new items?".
 * 3. **Enable photo posting** (Advanced) - `InputAdvancedPanel.jsx:33`. Complex
 *    always shows it; BASIC hides it whenever it is already on, per a dated
 *    comment in that file. So a basic account can turn photo posting on and
 *    never off.
 *
 * Everything else - the seven tabs, the four source-type rules, hashtags, item
 * text, filters, location, the rest of advanced - is the same inventory, from
 * the same `buildFeedSections()`. One data builder, two screens, so the shared
 * 95% cannot drift.
 *
 * OPEN - "SOURCE" OR "FEED" ON THIS SCREEN. The locked vocabulary says *source*
 * for an input, and this screen uses it. But legacy's complex UI says *feed*
 * throughout ("Feed URL", "Feed Active", "Delete Feed", "1 Feed", "Add Feed"),
 * and I5's ruling was that each UI keeps its own word. Extending that ruling
 * would make this screen say "feed" everywhere - which is a copy decision
 * across ~13 labels in a data file BOTH screens share, so it is flagged rather
 * than taken. See LEGACY-QUESTIONS.
 *
 * WIRING (production owns all of it): identical to `feed-settings.tsx` -
 * `InputEdit.jsx` keeps state, the dirty diff, save / cancel and the
 * `ErrorModule` plumbing. The only additions are the three props above, which
 * prod reads off `app.flags.routeViewType`, the load-time active state, and the
 * input record.
 */

import * as React from "react"

import { History, Stethoscope } from "lucide-react"

import {
  sourceLabel,
  sourceNetwork,
  type FeedSubtype,
  type SourceType,
} from "@/shadcn/components/settings/settings-vocab"
import { type SettingsSection } from "@/shadcn/components/settings/settings-form"
import {
  SettingsPage,
  type SettingsPageProps,
  SettingsAvatar,
} from "@/shadcn/components/settings/settings-page"

type SourceSettingsProps = Partial<SettingsPageProps> & {
  /**
   * REQUIRED HERE, unlike the preview kit, where it defaults to a mock
   * fixture. Prod builds this from the stored record; there is no fixture to
   * fall back to. See `settings-vocab.ts` for why the split exists.
   */
  sections: SettingsSection[]
  /** Drives the four source-type rules. */
  source?: SourceType
  /** RSS flavour. Label and URL example only. */
  subtype?: FeedSubtype
  /** This source's name, e.g. "Latest F1 News". */
  sourceName?: string
  /**
   * The picture the legacy avatar shows: `input.icon` for a source,
   * `social.icon` (else `social.src`) for a destination. Omitted, or failing
   * to load, the avatar falls back to initials.
   */
  avatarUrl?: string
  /** The route this source belongs to - the level above, and the back link. */
  routeName?: string
}

export default function SourceSettings({
  source = "rss",
  subtype,
  sourceName = "Latest F1 News",
  avatarUrl,
  routeName = "My New Route",
  sections,
  title,
  meta,
  backLabel,
  note,
  actions,
  deleteTitle = "Delete source",
  deleteDescription = "This removes the source from this route and stops it posting. Posts we already published stay up. Other sources on the route are unaffected.",
  deleteLabel = "Delete source",
  ...props
}: SourceSettingsProps) {
  return (
    <SettingsPage
      /* `complex: true` is what adds the update windows and keeps photo
         posting visible; `justActivated` is passed per state by the caller. */
      sections={sections}
      /*
       * The name leads and the settings noun sits under it (Emily,
       * 2026-10-01): the reader arrives knowing which source they opened, so
       * that is what the big line confirms. Same type as before, swapped.
       */
      title={title ?? sourceName}
      icon={
        <SettingsAvatar
          name={sourceName}
          avatarUrl={avatarUrl}
          network={sourceNetwork(source, subtype)}
        />
      }
      meta={meta ?? `Source settings · ${sourceLabel(source, subtype)}`}
      /* Back goes to the ROUTE, not to a list of automations: in complex this
         source only exists inside one. */
      backLabel={backLabel ?? routeName}
      /*
       * Two levels of settings apply to what this source posts, and the route's
       * are the ones a reader cannot see from here. Basic's note says "every
       * destination on this automation" because there is no level above it;
       * this one has to point up as well as down.
       */
      note={
        /*
         * `undefined` MEANS "NOT SPECIFIED", `null` MEANS "NONE". `??` cannot
         * tell the two apart, and a dismissal passes null — so under `??` the
         * note came straight back. `SettingsPageProps` states the contract;
         * this is the explicit check that keeps it.
         */
        note !== undefined
          ? note
          : `These settings apply to this source only. ${routeName} has its own settings that apply to every source on it.`
      }
      actions={
        actions ?? [
          { label: "History", icon: <History /> },
          { label: "Medic", icon: <Stethoscope /> },
        ]
      }
      deleteTitle={deleteTitle}
      deleteDescription={deleteDescription}
      deleteLabel={deleteLabel}
      {...props}
    />
  )
}
